import type {
  PublicShareManifest,
  SnapshotCanvasViewSettings,
  SnapshotGraphEdge,
  SnapshotItem,
  SnapshotPublishedLayout,
  SnapshotThemeColorSet,
  ShareTheme,
} from '@containd/shared';
import type { RenderDescriptor } from '@containd/template-render';
import {
  activeLayoutIsBuiltin,
  isGenericSnapshotItem,
} from './viewingTap';

/** Defaults when freezeVersion is 0 / missing (legacy demos). */
export const LEGACY_VIEW_SETTINGS: SnapshotCanvasViewSettings = {
  showSvgBackground: true,
  showGraphContent: true,
  showDraggableItems: true,
  showItemLabels: true,
  showItemChildrenCount: true,
  showItemFrame: true,
  showItemBackground: true,
  showItemBadges: true,
  showItemImage: true,
  itemsOpacityBackgroundMode: 0,
  itemsOpacityOtherModes: 1,
  clipSvgToCanvas: true,
};

const FALLBACK_PALETTE: Record<ShareTheme, SnapshotThemeColorSet> = {
  light: {
    background: '#F5F5F5',
    surface: '#FFFFFF',
    text: '#000000',
    primary: '#0a7ea4',
    accent: '#6F42C1',
    border: '#E0E0E0',
    itemBackground: '#ffffff',
    itemText: '#000000',
    itemBorder: '#000000',
    itemDropShadow: '#000000',
  },
  dark: {
    background: '#1C1C1E',
    surface: '#2C2C2E',
    text: '#FFFFFF',
    primary: '#38bdf8',
    accent: '#AF52DE',
    border: '#48484A',
    itemBackground: '#2d2d2d',
    itemText: '#ffffff',
    itemBorder: '#555555',
    itemDropShadow: '#09090b',
  },
};

export function resolveFrozenViewSettings(
  manifest: PublicShareManifest,
): SnapshotCanvasViewSettings {
  if ((manifest.freezeVersion ?? 0) >= 1 && manifest.canvasViewSettings) {
    const settings = manifest.canvasViewSettings;
    return {
      ...settings,
      // Legacy freezes omit this — match host default (clipped).
      clipSvgToCanvas: settings.clipSvgToCanvas !== false,
    };
  }
  return LEGACY_VIEW_SETTINGS;
}

/**
 * Active theme surface colors from the freeze.
 * Prefer frozen palettes for both modes; pick by `manifest.theme`.
 */
export function resolveSharePaintColors(manifest: PublicShareManifest): {
  theme: ShareTheme;
  palette: SnapshotThemeColorSet;
  /** Outside the canvas (viewport letterbox). Frozen palette `background`. */
  viewportBackground: string;
  /** Canvas page fill. Frozen palette `surface`. */
  canvasBackground: string;
  surface: string;
  text: string;
  primary: string;
  /** Default graph edge stroke (Containd theme accent). */
  accent: string;
  /** Outside-stroke page frame (Containd 2px overlay; frozen palette `border`). */
  canvasBorder: string;
} {
  const theme: ShareTheme = manifest.theme === 'dark' ? 'dark' : 'light';
  const frozen = manifest.themeColorPalettes?.[theme];
  const fallback = FALLBACK_PALETTE[theme];
  const palette: SnapshotThemeColorSet = { ...fallback, ...(frozen ?? {}) };
  const viewportBackground =
    palette.background || fallback.background || '#F5F5F5';
  const canvasBackground =
    palette.surface || fallback.surface || viewportBackground;
  return {
    theme,
    palette,
    viewportBackground,
    canvasBackground,
    surface: canvasBackground,
    text: palette.text || fallback.text || '#000000',
    primary: palette.primary || fallback.primary || '#0a7ea4',
    accent:
      palette.accent ||
      fallback.accent ||
      palette.primary ||
      fallback.primary ||
      '#6F42C1',
    canvasBorder: palette.border || fallback.border || '#E0E0E0',
  };
}

export function edgesForCanvas(
  manifest: PublicShareManifest,
  canvasParentUuid: string | null,
): SnapshotGraphEdge[] {
  const settings = resolveFrozenViewSettings(manifest);
  if (!settings.showGraphContent) return [];
  const chunk = manifest.canvases.find((c) => c.parentUuid === canvasParentUuid);
  const ids = chunk?.graphEdgeIds;
  const all = manifest.graphEdges ?? {};
  if (ids && ids.length > 0) {
    return ids.map((id) => all[id]).filter(Boolean) as SnapshotGraphEdge[];
  }
  return Object.values(all).filter(
    (e) => e.canvasParentUuid === canvasParentUuid,
  );
}

export function edgeToSvgPath(edge: SnapshotGraphEdge): string | null {
  if (edge.pathD && edge.pathD.trim()) return edge.pathD;
  if (edge.points && edge.points.length >= 2) {
    const [first, ...rest] = edge.points;
    return `M ${first!.x} ${first!.y} ${rest.map((p) => `L ${p.x} ${p.y}`).join(' ')}`;
  }
  return null;
}

export function bakedDescriptorsFor(
  manifest: PublicShareManifest,
  itemUuid: string,
  layoutIndex: number,
): RenderDescriptor[] | null {
  const key = `${itemUuid}:${layoutIndex}`;
  const baked = manifest.bakedDescriptors?.[key];
  if (!Array.isArray(baked) || baked.length === 0) return null;
  return baked as RenderDescriptor[];
}

/** Layouts published for an item — never requires TemplateDocuments. */
export function publishedLayoutsForItem(
  manifest: PublicShareManifest,
  item: SnapshotItem,
): SnapshotPublishedLayout[] {
  if (item.publishedLayouts && item.publishedLayouts.length > 0) {
    return item.publishedLayouts;
  }
  const baked = manifest.bakedDescriptors ?? {};
  const indices = new Set<number>();
  const prefix = `${item.uuid}:`;
  for (const key of Object.keys(baked)) {
    if (!key.startsWith(prefix)) continue;
    const idx = Number(key.slice(prefix.length));
    if (Number.isInteger(idx) && idx >= 0) indices.add(idx);
  }
  if (indices.size > 0) {
    return [...indices]
      .sort((a, b) => a - b)
      .map((index) => ({ index, label: `Layout ${index + 1}` }));
  }
  return [{ index: 0 }];
}

export function layoutCountForItem(
  manifest: PublicShareManifest,
  item: SnapshotItem,
): number {
  return Math.max(1, publishedLayoutsForItem(manifest, item).length);
}

/**
 * Step among frozen `publishedLayouts` indices (not a dense 0..count range).
 * Disabled / unpublished layouts are not in the list, so cycling skips them.
 */
export function nextPublishedLayoutIndex(
  manifest: PublicShareManifest,
  item: SnapshotItem,
  currentIndex: number,
  delta: number,
): number {
  const indices = publishedLayoutsForItem(manifest, item).map((l) => l.index);
  if (indices.length === 0) return currentIndex;
  if (indices.length === 1) return indices[0]!;
  let pos = indices.indexOf(currentIndex);
  if (pos < 0) pos = 0;
  const n = indices.length;
  return indices[(((pos + delta) % n) + n) % n]!;
}

/** 0-based position among published layouts, for "n/m" chrome. */
export function publishedLayoutSlot(
  manifest: PublicShareManifest,
  item: SnapshotItem,
  layoutIndex: number,
): number {
  const pos = publishedLayoutsForItem(manifest, item).findIndex(
    (l) => l.index === layoutIndex,
  );
  return pos >= 0 ? pos : 0;
}

/**
 * freezeVersion >= 1: never re-run the template pipeline.
 * Paint baked descriptors, else generic shell — unknown templates are fine.
 */
export function paintRequiresPipelineFallback(
  manifest: PublicShareManifest,
): boolean {
  return (manifest.freezeVersion ?? 0) < 1;
}

/**
 * Editor-only title+amount descriptors from `buildBuiltinLayoutDescriptors`.
 * Live canvas never paints these — share must use GenericItemHost instead.
 */
export function isEditorBuiltinDescriptorBake(
  descriptors: RenderDescriptor[] | null | undefined,
): boolean {
  if (!descriptors || descriptors.length === 0) return false;
  return descriptors.every(
    (d) =>
      typeof d.nodeId === 'string' && d.nodeId.includes('::builtin-'),
  );
}

function itemHasSvgShapeSurface(item: SnapshotItem): boolean {
  return Boolean(item.svgShapeSettings || item.svgGraphicsMediaId);
}

/**
 * Prefer ItemCardHost (baked descriptors) over GenericItemHost when a real
 * (non-editor-builtin) bake is present. renderAsBuiltin / SVG-shape layouts
 * always use the generic surface.
 */
export function shouldUseGenericSurface(
  manifest: PublicShareManifest,
  item: SnapshotItem,
  layoutIndex: number,
): boolean {
  if (isGenericSnapshotItem(item)) return true;
  const published = item.publishedLayouts?.find((l) => l.index === layoutIndex);
  if (published?.renderAsBuiltin === true) return true;
  const baked = bakedDescriptorsFor(manifest, item.uuid, layoutIndex);
  if (baked && isEditorBuiltinDescriptorBake(baked)) return true;
  if (baked) return false;
  // freezeVersion >= 1 without bake → generic shell (unknown template / builtin).
  if (!paintRequiresPipelineFallback(manifest)) return true;
  if (!manifest.templateDocuments?.[item.templateId]) return true;
  return activeLayoutIsBuiltin(manifest, item, layoutIndex);
}

/** Non-generic items missing bake on a freezeVersion >= 1 share. */
export function missingBakeItems(
  manifest: PublicShareManifest,
): SnapshotItem[] {
  if (paintRequiresPipelineFallback(manifest)) return [];
  return manifest.items.filter((item) => {
    if (isGenericSnapshotItem(item)) return false;
    if (itemHasSvgShapeSurface(item)) return false;
    const layouts = publishedLayoutsForItem(manifest, item);
    return !layouts.some((l) => {
      if (l.renderAsBuiltin === true) return true;
      const baked = bakedDescriptorsFor(manifest, item.uuid, l.index);
      return Boolean(baked && !isEditorBuiltinDescriptorBake(baked));
    });
  });
}
